import React from "react";
import { Helmet } from "react-helmet";
import {
  FiArrowLeft,
  FiDatabase,
  FiGlobe,
  FiLock,
  FiMail,
  FiShield,
  FiUserCheck,
} from "react-icons/fi";

import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Dropgear IO Private Limited</title>

        <meta
          name="description"
          content="Privacy Policy for Dropgear IO Private Limited explaining how we collect, use, protect, and manage information when you use our website and services."
        />
      </Helmet>

      <Header />

      <main className="dg-privacy-page">

        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="dg-privacy-hero">

          <div className="dg-privacy-container">

            <span className="dg-privacy-eyebrow">
              LEGAL & PRIVACY
            </span>

            <h1>
              Privacy
              <span> Policy</span>
            </h1>

            <p>
              We believe responsible technology starts with transparency.
              This Privacy Policy explains how Dropgear IO Private Limited
              collects, uses, stores, and manages information when you
              interact with our website, products, and services.
            </p>

            <div className="dg-privacy-updated">
              <strong>Last Updated:</strong> August 10, 2026
            </div>

          </div>

        </section>


        {/* =====================================================
            CONTENT
        ===================================================== */}

        <section className="dg-privacy-content">

          <div className="dg-privacy-container">

            <div className="dg-privacy-layout">

              {/* =================================================
                  SIDE NAVIGATION
              ================================================= */}

              <aside className="dg-privacy-sidebar">

                <div className="dg-privacy-sidebar-inner">

                  <span>
                    ON THIS PAGE
                  </span>

                  <a href="#overview">
                    Overview
                  </a>

                  <a href="#information">
                    Information We Collect
                  </a>

                  <a href="#usage">
                    How We Use Information
                  </a>

                  <a href="#ai">
                    AI & Automated Systems
                  </a>

                  <a href="#sharing">
                    Information Sharing
                  </a>

                  <a href="#cookies">
                    Cookies & Technologies
                  </a>

                  <a href="#retention">
                    Data Retention
                  </a>

                  <a href="#security">
                    Data Security
                  </a>

                  <a href="#rights">
                    Your Rights
                  </a>

                  <a href="#children">
                    Children's Privacy
                  </a>

                  <a href="#changes">
                    Policy Changes
                  </a>

                  <a href="#contact">
                    Contact Us
                  </a>

                </div>

              </aside>


              {/* =================================================
                  MAIN ARTICLE
              ================================================= */}

              <article className="dg-privacy-article">

                {/* =================================================
                    OVERVIEW
                ================================================= */}

                <section id="overview">

                  <div className="dg-privacy-section-icon">
                    <FiShield />
                  </div>

                  <h2>
                    1. Overview
                  </h2>

                  <p>
                    This Privacy Policy describes how
                    <strong> Dropgear IO Private Limited </strong>
                    ("Dropgear IO", "we", "us", or "our") handles
                    information collected through our website,
                    applications, products, platforms, and related
                    services.
                  </p>

                  <p>
                    By accessing or using our services, you acknowledge
                    that you have read and understood this Privacy Policy.
                    If you do not agree with the practices described here,
                    please discontinue use of the relevant services.
                  </p>

                </section>


                {/* =================================================
                    INFORMATION
                ================================================= */}

                <section id="information">

                  <div className="dg-privacy-section-icon">
                    <FiDatabase />
                  </div>

                  <h2>
                    2. Information We Collect
                  </h2>

                  <p>
                    The information we collect depends on how you interact
                    with Dropgear IO.
                  </p>


                  <h3>
                    Information You Provide
                  </h3>

                  <p>
                    You may provide information when you create an account,
                    request a demo, contact our team, submit a form, or
                    otherwise communicate with us.
                  </p>

                  <ul>
                    <li>Name and contact information</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                    <li>Company or organization information</li>
                    <li>Job title or professional information</li>
                    <li>Information included in messages or requests</li>
                    <li>Account credentials and authentication information</li>
                  </ul>


                  <h3>
                    Information Collected Automatically
                  </h3>

                  <p>
                    When you visit our website or use our services, certain
                    technical information may be collected automatically.
                  </p>

                  <ul>
                    <li>IP address</li>
                    <li>Browser type and version</li>
                    <li>Device type</li>
                    <li>Operating system</li>
                    <li>Pages and features accessed</li>
                    <li>Approximate usage information</li>
                    <li>Date and time of interactions</li>
                  </ul>

                </section>


                {/* =================================================
                    USAGE
                ================================================= */}

                <section id="usage">

                  <div className="dg-privacy-section-icon">
                    <FiUserCheck />
                  </div>

                  <h2>
                    3. How We Use Information
                  </h2>

                  <p>
                    We may use information we collect for legitimate
                    business and operational purposes, including:
                  </p>

                  <ul>
                    <li>
                      Providing and operating our website and services.
                    </li>

                    <li>
                      Creating and managing user accounts.
                    </li>

                    <li>
                      Responding to enquiries, support requests, and demo
                      requests.
                    </li>

                    <li>
                      Understanding how our services are used.
                    </li>

                    <li>
                      Improving our products, systems, and user experience.
                    </li>

                    <li>
                      Detecting, preventing, and addressing technical or
                      security issues.
                    </li>

                    <li>
                      Communicating with users regarding our services.
                    </li>

                    <li>
                      Complying with applicable legal or regulatory
                      requirements.
                    </li>
                  </ul>

                </section>


                {/* =================================================
                    AI
                ================================================= */}

                <section id="ai">

                  <div className="dg-privacy-section-icon">
                    <FiGlobe />
                  </div>

                  <h2>
                    4. AI & Automated Systems
                  </h2>

                  <p>
                    Dropgear IO develops and provides AI-powered software,
                    automation systems, and intelligent workflow
                    technologies.
                  </p>

                  <p>
                    Depending on the product or service being used,
                    information may be processed by automated systems to
                    perform functions requested by users or organizations.
                  </p>

                  <p>
                    We aim to design our AI systems with appropriate
                    consideration for privacy, data minimization,
                    transparency, and responsible system operation.
                  </p>

                  <p>
                    Customers using Dropgear IO services are responsible for
                    ensuring that the information they provide to our systems
                    is appropriate for the intended use and that they have
                    the necessary rights or permissions to provide such
                    information.
                  </p>

                  <div className="dg-privacy-highlight">

                    <strong>
                      Important:
                    </strong>

                    <span>
                      Do not submit highly sensitive, confidential, or
                      regulated information through a Dropgear IO service
                      unless the relevant service documentation or agreement
                      specifically permits such processing.
                    </span>

                  </div>

                </section>


                {/* =================================================
                    SHARING
                ================================================= */}

                <section id="sharing">

                  <div className="dg-privacy-section-icon">
                    <FiShield />
                  </div>

                  <h2>
                    5. Information Sharing
                  </h2>

                  <p>
                    We do not treat personal information as a product to be
                    sold. Information may be shared or made accessible when
                    reasonably necessary to operate our business and provide
                    our services.
                  </p>

                  <h3>
                    Service Providers
                  </h3>

                  <p>
                    We may work with third-party service providers that
                    support functions such as hosting, infrastructure,
                    analytics, communications, security, payment processing,
                    or other business operations.
                  </p>

                  <h3>
                    Legal Requirements
                  </h3>

                  <p>
                    We may disclose information where reasonably necessary
                    to comply with applicable law, legal process, regulatory
                    requests, or to protect our rights, users, systems, or
                    property.
                  </p>

                  <h3>
                    Business Transfers
                  </h3>

                  <p>
                    Information may be transferred as part of a merger,
                    acquisition, restructuring, financing, sale of assets,
                    or similar corporate transaction, subject to applicable
                    legal requirements.
                  </p>

                </section>


                {/* =================================================
                    COOKIES
                ================================================= */}

                <section id="cookies">

                  <div className="dg-privacy-section-icon">
                    <FiGlobe />
                  </div>

                  <h2>
                    6. Cookies & Similar Technologies
                  </h2>

                  <p>
                    Our website may use cookies and similar technologies to
                    support essential functionality, understand website
                    usage, remember preferences, and improve the experience.
                  </p>

                  <p>
                    Depending on how our website is configured, third-party
                    analytics or service providers may also use similar
                    technologies.
                  </p>

                  <p>
                    You can generally control cookies through your browser
                    settings. Disabling certain cookies may affect the
                    availability or functionality of some website features.
                  </p>

                </section>


                {/* =================================================
                    RETENTION
                ================================================= */}

                <section id="retention">

                  <div className="dg-privacy-section-icon">
                    <FiDatabase />
                  </div>

                  <h2>
                    7. Data Retention
                  </h2>

                  <p>
                    We retain information for as long as reasonably necessary
                    to provide the requested services, maintain business
                    records, resolve disputes, enforce agreements, satisfy
                    legal obligations, or fulfil other legitimate purposes.
                  </p>

                  <p>
                    Retention periods may vary depending on the nature of
                    the information and the purpose for which it was
                    collected.
                  </p>

                </section>


                {/* =================================================
                    SECURITY
                ================================================= */}

                <section id="security">

                  <div className="dg-privacy-section-icon">
                    <FiLock />
                  </div>

                  <h2>
                    8. Data Security
                  </h2>

                  <p>
                    We take reasonable steps to protect information against
                    unauthorized access, misuse, alteration, disclosure, or
                    destruction.
                  </p>

                  <p>
                    Security practices may include access controls,
                    technical safeguards, monitoring, secure development
                    practices, and other measures appropriate to the nature
                    of the information and services involved.
                  </p>

                  <p>
                    However, no internet transmission or storage system can
                    be guaranteed to be completely secure. Accordingly, we
                    cannot guarantee absolute security of information.
                  </p>

                </section>


                {/* =================================================
                    RIGHTS
                ================================================= */}

                <section id="rights">

                  <div className="dg-privacy-section-icon">
                    <FiUserCheck />
                  </div>

                  <h2>
                    9. Your Privacy Rights
                  </h2>

                  <p>
                    Depending on your location and applicable law, you may
                    have certain rights relating to your personal information.
                  </p>

                  <p>
                    These may include rights to request access to,
                    correction of, deletion of, or information about the
                    processing of your personal information, subject to
                    applicable legal limitations.
                  </p>

                  <p>
                    To make a privacy-related request, contact us using the
                    details provided at the end of this Privacy Policy.
                  </p>

                </section>


                {/* =================================================
                    CHILDREN
                ================================================= */}

                <section id="children">

                  <div className="dg-privacy-section-icon">
                    <FiShield />
                  </div>

                  <h2>
                    10. Children's Privacy
                  </h2>

                  <p>
                    Our services are intended for businesses, professionals,
                    and general users who are legally able to use the
                    relevant services.
                  </p>

                  <p>
                    We do not knowingly design our services to collect
                    personal information from children in circumstances
                    where such collection is prohibited by applicable law.
                  </p>

                  <p>
                    If you believe that a child has provided personal
                    information to us improperly, please contact us so that
                    we can review the situation and take appropriate action.
                  </p>

                </section>


                {/* =================================================
                    THIRD PARTY
                ================================================= */}

                <section>

                  <div className="dg-privacy-section-icon">
                    <FiGlobe />
                  </div>

                  <h2>
                    11. Third-Party Websites & Services
                  </h2>

                  <p>
                    Our website or services may contain links to third-party
                    websites, applications, or services.
                  </p>

                  <p>
                    These third parties operate independently and may have
                    their own privacy policies and practices. We encourage
                    you to review their privacy policies before providing
                    personal information.
                  </p>

                </section>


                {/* =================================================
                    INTERNATIONAL
                ================================================= */}

                <section>

                  <div className="dg-privacy-section-icon">
                    <FiGlobe />
                  </div>

                  <h2>
                    12. International Data Processing
                  </h2>

                  <p>
                    Depending on the infrastructure and service providers
                    used to operate our products, information may be
                    processed or stored in locations outside your state,
                    region, or country.
                  </p>

                  <p>
                    Where applicable, we seek to handle such processing in
                    accordance with relevant legal requirements and
                    appropriate contractual or technical safeguards.
                  </p>

                </section>


                {/* =================================================
                    CHANGES
                ================================================= */}

                <section id="changes">

                  <div className="dg-privacy-section-icon">
                    <FiShield />
                  </div>

                  <h2>
                    13. Changes to This Privacy Policy
                  </h2>

                  <p>
                    We may update this Privacy Policy from time to time to
                    reflect changes in our services, technology, business
                    practices, or applicable legal requirements.
                  </p>

                  <p>
                    When changes are made, the updated version will be
                    published on this page with a revised "Last Updated"
                    date.
                  </p>

                  <p>
                    We encourage you to review this page periodically to
                    remain informed about our privacy practices.
                  </p>

                </section>


                {/* =================================================
                    CONTACT
                ================================================= */}

                <section id="contact">

                  <div className="dg-privacy-section-icon">
                    <FiMail />
                  </div>

                  <h2>
                    14. Contact Us
                  </h2>

                  <p>
                    If you have questions, concerns, or requests regarding
                    this Privacy Policy or the way Dropgear IO handles
                    information, please contact our team.
                  </p>

                  <div className="dg-privacy-contact-card">

                    <div>
                      <span>
                        COMPANY
                      </span>

                      <strong>
                        Dropgear IO Private Limited
                      </strong>
                    </div>


                    <div>
                      <span>
                        EMAIL
                      </span>

                      <a href="mailto:admin@aidropgerio.in">
                        admin@aidropgerio.in
                      </a>
                    </div>

                  </div>

                </section>


                {/* =================================================
                    FINAL NOTE
                ================================================= */}

                <div className="dg-privacy-final">

                  <FiShield />

                  <p>
                    Dropgear IO is committed to building useful AI systems
                    with privacy, transparency, and responsible technology
                    practices in mind.
                  </p>

                </div>

              </article>

            </div>

          </div>

        </section>


        {/* =====================================================
            BACK TO TOP
        ===================================================== */}

        <div className="dg-privacy-back">

          <div className="dg-privacy-container">

            <a href="#">
              <FiArrowLeft />
              Back to top
            </a>

          </div>

        </div>

      </main>

      <Footer />
    </>
  );
};

export default PrivacyPolicy;

